import React, { useMemo } from "react";
import { Grid, Typography } from "@mui/material";
import ReactApexChart from "react-apexcharts";
import { GraphContainer } from "@Styled/Pages/Report.styled";
import {
    chartColors,
    titleCase,
    orderWithUnknownLast,
} from "../../Utils/chartHelpers";
import { useTranslation } from "react-i18next";

type DistributionRow = Record<string, string | number>; // e.g. { political_view: "Left", count: 38 }
type Distributions = Record<string, DistributionRow[]>; // e.g. { political_view_distribution: [...] }

type ByVoteRow = {
    variable_key: string; // e.g. "political_view"
    option: string; // e.g. "Inclusion & diversity"
    values: Record<string, number>; // e.g. { Left: 29, Center: 32, Right: 34, unknown: 22 }
};

interface Props {
    distributions: Distributions | null | undefined;
    byVote: ByVoteRow[] | null | undefined;
}

const CustomVariableCharts: React.FC<Props> = ({ distributions, byVote }) => {
    const { t } = useTranslation();
    // Always call hooks first
    const processedDistributions = useMemo(() => {
        if (!distributions || !byVote) return [];

        return Object.entries(distributions).map(([distKey, distData]) => {
            const variableKey = distKey.replace(/_distribution$/, "");
            const prettyName = titleCase(variableKey);

            const categoryField = variableKey;
            const cleanedDistData = distData.filter((row) => {
                const label = String(row[categoryField] ?? "").trim();
                const count = Number(row["count"] ?? 0);
                if (!label) {
                    return count > 0;
                }
                return !(
                    label.toLowerCase() === "unknown" && (count ?? 0) === 0
                );
            });
            const categoriesOverall = cleanedDistData.map((row) =>
                String(row[categoryField] ?? "")
            );
            const dataOverall = cleanedDistData.map((row) =>
                Number(row["count"] ?? 0)
            );

            const overallOptions: ApexCharts.ApexOptions = {
                chart: { type: "bar", toolbar: { show: false } },
                colors: chartColors,
                plotOptions: {
                    bar: {
                        columnWidth: "55%",
                        borderRadius: 4,
                        distributed: true,
                        dataLabels: {
                            position: "top",
                        },
                    },
                },
                dataLabels: {
                    enabled: true,
                    formatter: (val: number) => `${val}`,
                    offsetY: -12,
                    style: { colors: ["#000000"] },
                },
                xaxis: {
                    categories: categoriesOverall,
                    labels: { rotate: -20, trim: true },
                    title: { text: prettyName },
                },
                yaxis: {
                    title: { text: t("reports.customVariables.yAxisLabel") },
                },
                legend: { show: true, position: "bottom" as const },
                tooltip: {
                    y: {
                        formatter: (val: number) =>
                            t("reports.customVariables.tooltipVotes", {
                                count: val,
                            }),
                    },
                },
            };

            const overallSeries = [
                {
                    name: t("reports.customVariables.series.overall", {
                        variable: prettyName,
                    }),
                    data: dataOverall,
                },
            ];

            const voteBreakdowns = byVote.filter(
                (v) => v.variable_key === variableKey
            );
            const allCatsSet = new Set<string>();
            voteBreakdowns.forEach((vb) =>
                Object.keys(vb.values).forEach((c) => allCatsSet.add(c))
            );
            const groupedCategoriesRaw = orderWithUnknownLast(
                Array.from(allCatsSet)
            );
            const groupedCategories = groupedCategoriesRaw.filter((cat) => {
                if (!cat || cat.toLowerCase() !== "unknown") {
                    return true;
                }
                const total = voteBreakdowns.reduce(
                    (sum, vb) => sum + (vb.values[cat] ?? 0),
                    0
                );
                return total > 0;
            });

            const groupedSeries = voteBreakdowns.map((vb, idx) => ({
                name: vb.option,
                data: groupedCategories.map((cat) => vb.values[cat] ?? 0),
                color: chartColors[idx % chartColors.length],
            }));

            const groupedOptions: ApexCharts.ApexOptions = {
                chart: {
                    type: "bar",
                    stacked: false,
                    toolbar: { show: false },
                },
                colors: chartColors,
                plotOptions: {
                    bar: {
                        columnWidth: "55%",
                        borderRadius: 4,
                        dataLabels: {
                            position: "top",
                        },
                    },
                },
                dataLabels: {
                    enabled: true,
                    style: { colors: ["#000000"] },
                    formatter: (val: number) => `${val}`,
                    offsetY: -12,
                },
                xaxis: {
                    categories: groupedCategories,
                    labels: { rotate: -20, trim: true },
                    title: { text: prettyName },
                },
                yaxis: {
                    title: { text: t("reports.customVariables.yAxisLabel") },
                },
                legend: { position: "bottom" as const, show: true },
                tooltip: {
                    y: {
                        formatter: (val: number) =>
                            t("reports.customVariables.tooltipVotes", {
                                count: val,
                            }),
                    },
                },
            };

            return {
                variableKey,
                prettyName,
                overallOptions,
                overallSeries,
                groupedOptions,
                groupedSeries,
            };
        });
    }, [distributions, byVote, t]);

    // Safe return after hook calls
    if (!distributions || !byVote || processedDistributions.length === 0)
        return null;

    return (
        <Grid container spacing={3}>
            {processedDistributions.map(
                ({
                    variableKey,
                    prettyName,
                    overallOptions,
                    overallSeries,
                    groupedOptions,
                    groupedSeries,
                }) => (
                    <React.Fragment key={variableKey}>
                        <Grid item xs={12}>
                            <GraphContainer>
                                <Typography
                                    variant="subtitle1"
                                    gutterBottom
                                    align="center"
                                >
                                    {t(
                                        "reports.customVariables.series.overall",
                                        {
                                            variable: prettyName,
                                        }
                                    )}
                                </Typography>
                                <ReactApexChart
                                    options={overallOptions}
                                    series={overallSeries as any}
                                    type="bar"
                                    height={320}
                                />
                            </GraphContainer>
                        </Grid>

                        <Grid item xs={12}>
                            <GraphContainer>
                                <Typography
                                    variant="subtitle1"
                                    gutterBottom
                                    align="center"
                                >
                                    {t(
                                        "reports.customVariables.series.byOption",
                                        {
                                            variable: prettyName,
                                        }
                                    )}
                                </Typography>
                                <ReactApexChart
                                    options={groupedOptions}
                                    series={groupedSeries as any}
                                    type="bar"
                                    height={320}
                                />
                            </GraphContainer>
                        </Grid>
                    </React.Fragment>
                )
            )}
        </Grid>
    );
};

export default CustomVariableCharts;
